import { useEffect } from 'react'
import { Grid } from '@mui/material'
import { useFormikContext } from 'formik'
import { t } from 'i18next'

import AutoCompleteField from 'src/components/shared/ui/form/fields/AutoCompleteField'
import RequiredText from 'src/components/views/EscritosLexnet/components/shared/components/RequiredText'
import { OrganoDestinoFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/organoDestinoTypes'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { Orden as OrdenMaster, TipoMensaje } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import {
  useGetOrganosJudiciales,
  useGetPartidosJudiciales,
  useGetProvincias,
  useGetDestinoComunidades
} from 'src/hooks/fetch/useCatalogoGeneralFilters'
import { Comunidad, OrganoJudicial } from 'src/interfaces/Catalog'
import { destinoFormStyles as styles } from './DestinoFormStyles'

function DestinoForm(): JSX.Element {
  const { values, setFieldValue } = useFormikContext<OrganoDestinoFormValues>()
  const { tipoMensaje, isMonitorio } = useTipoMensajeContext()

  const organoFilter = [
    values[FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL],
    isMonitorio ? TipoMensaje.MONITORIO_CATALOGO : tipoMensaje
  ]
  const claseRegistro = isMonitorio

  const itemsOrganosJudiciales = useGetOrganosJudiciales(
    FormField.ORGANO_DESTINO_ORGANO_JUDICIAL,
    isMonitorio ? [...organoFilter, OrdenMaster.CIVIL] : organoFilter,
    isMonitorio,
    claseRegistro
  )

  const itemsComunidades = useGetDestinoComunidades(FormField.COMUNIDAD_AUTONOMA)

  function handleFieldChange(field: string) {
    if (field === FormField.COMUNIDAD_AUTONOMA) {
      setFieldValue(FormField.PROVINCIA, null, false)
      setFieldValue(FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL, null, false)
      setFieldValue(FormField.ORGANO_DESTINO_ORGANO_JUDICIAL, null, false)
      setFieldValue(FormField.ORGANO_DESTINO_ORDEN_JUDICIAL, null, false)
    }

    if (field === FormField.PROVINCIA) {
      setFieldValue(FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL, null, false)
      setFieldValue(FormField.ORGANO_DESTINO_ORGANO_JUDICIAL, null, false)
      setFieldValue(FormField.ORGANO_DESTINO_ORDEN_JUDICIAL, null, false)
    }

    if (field === FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL) {
      setFieldValue(FormField.ORGANO_DESTINO_ORGANO_JUDICIAL, null, false)
      setFieldValue(FormField.ORGANO_DESTINO_ORDEN_JUDICIAL, null, false)
    }

    if (field === FormField.ORGANO_DESTINO_ORGANO_JUDICIAL) {
      setFieldValue(FormField.ORGANO_DESTINO_ORDEN_JUDICIAL, null, false)
    }
  }

  useEffect(() => {
    const organo: OrganoJudicial | null = values[FormField.ORGANO_DESTINO_ORGANO_JUDICIAL]

    if (organo != null) {
      if (organo.ordenes && organo.ordenes.length === 1) {
        setFieldValue(FormField.ORGANO_DESTINO_ORDEN_JUDICIAL, organo.ordenes[0], false)
      }
    } else {
      setFieldValue(FormField.ORGANO_DESTINO_ORDEN_JUDICIAL, null, false)
    }

    // eslint-disable-next-line
  }, [values[FormField.ORGANO_DESTINO_ORGANO_JUDICIAL]])

  return (
    <>
      <RequiredText />
      <Grid container spacing={2} sx={styles.destinoForm}>
        <Grid item xs={7}>
          <AutoCompleteField
            id={FormField.COMUNIDAD_AUTONOMA}
            items={
              isMonitorio
                ? itemsComunidades?.filter((c: Comunidad) => c.monitorio === true)
                : itemsComunidades
            }
            onChange={handleFieldChange}
            label={t('form.field.comunidad')}
            itemName="descripcion"
            required
          />
        </Grid>
        <Grid item xs={7} display={values[FormField.COMUNIDAD_AUTONOMA] ? 'unset' : 'none'}>
          <AutoCompleteField
            id={FormField.PROVINCIA}
            items={useGetProvincias(FormField.PROVINCIA, values[FormField.COMUNIDAD_AUTONOMA])}
            onChange={handleFieldChange}
            label={t('form.field.provincia')}
            itemName="descripcion"
            disabled={values[FormField.COMUNIDAD_AUTONOMA] === null}
            required
          />
        </Grid>
        <Grid item xs={7} display={values[FormField.PROVINCIA] ? 'unset' : 'none'}>
          <AutoCompleteField
            id={FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL}
            items={useGetPartidosJudiciales(
              FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL,
              values[FormField.PROVINCIA]
            )}
            onChange={handleFieldChange}
            label={t('form.field.partido_judicial')}
            itemName="nombrePartido"
            disabled={values[FormField.PROVINCIA] === null}
            required
          />
        </Grid>
        <Grid
          item
          xs={7}
          display={values[FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL] ? 'unset' : 'none'}
        >
          <AutoCompleteField
            id={FormField.ORGANO_DESTINO_ORGANO_JUDICIAL}
            items={itemsOrganosJudiciales}
            onChange={handleFieldChange}
            label={t('form.field.organo_judicial')}
            itemName="nombre"
            itemKey="id"
            disabled={values[FormField.ORGANO_DESTINO_PARTIDO_JUDICIAL] === null}
            required
          />
        </Grid>
        <Grid
          item
          xs={7}
          display={values[FormField.ORGANO_DESTINO_ORGANO_JUDICIAL] ? 'unset' : 'none'}
        >
          <AutoCompleteField
            id={FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}
            items={values[FormField.ORGANO_DESTINO_ORGANO_JUDICIAL]?.ordenes || []}
            onChange={handleFieldChange}
            label={t('form.field.orden')}
            itemName="descripcion"
            disabled={values[FormField.ORGANO_DESTINO_ORGANO_JUDICIAL] === null}
            required
          />
        </Grid>
      </Grid>
    </>
  )
}

export default DestinoForm
